import { Table, Typography } from 'antd';

import { formatCalculatedValue } from '../../utils/formulaTokens';
import { drivenValuesReviewPath } from '../../utils/drivenChanges';
import ExternalLink from '../common/ExternalLink';

/**
 * The body of the "existing values will be replaced" confirmation the card
 * editor raises before a save that turns an element driven (calculated, or
 * driven from another card) or re-points a driven one. The API backfills
 * every item of the card the moment the definition saves, so this is the
 * last moment the typed values exist — the dialog names how many, shows a
 * few real rows (what is there now, what the rule will write), and opens the
 * full list filtered to exactly those items for anyone who wants to look
 * before deciding.
 *
 * `mode='update'` (keepr-api KPR-1057): the same content when a bundle update
 * would work an element out another way (ReviewUpdateDialog) — the same
 * count and sample, in the update's words ("when you take this change").
 * In either mode it says when the preview stopped short (`incomplete`: a
 * partial sample, the count complete) and when the calculation reads
 * something hidden from the person (`sampleReason: 'hidden'`: no sample).
 *
 * Rendered inside App.useApp().modal.confirm — the content is a plain
 * component so it can be tested and reused without the modal.
 */
export default function DrivenBackfillWarning({ previews = [], collectionId, cardId, mode = 'save' }) {
	const update = mode === 'update';
	return (
		<div className='driven-backfill-warning'>
			{previews.map(({ element, preview }) => {
				const label = element?.label?.singular || (typeof element?.label === 'string' ? element.label : '') || element?.name;
				const kind = preview?.kind === 'formula' ? 'calculated' : 'driven from another card';
				const rows = (preview?.sample || []).map(row => ({ key: row._id, ...row }));
				const reviewPath = drivenValuesReviewPath(collectionId, cardId, element?.name);
				const counted = Number.isFinite(preview?.withValues);
				const withValues = counted ? preview.withValues : 0;
				const total = Number.isFinite(preview?.total) ? preview.total : null;
				const noun = update ? 'record' : 'item';
				const heldWords = !counted ? `keepr could not count the ${noun}s holding a value in it` : `${withValues === 1 ? `1 ${noun} holds` : `${withValues.toLocaleString()} ${noun}s hold`} a value in it today${total === null ? '' : ` (of ${total.toLocaleString()} in total)`}`;
				const hidden = preview?.sampleReason === 'hidden';
				return (
					<div key={element?.name} className='driven-backfill-warning__element'>
						<Typography.Paragraph>
							{update ? (
								<><strong>{label}</strong> is worked out another way in this version ({kind}). {heldWords}; those values will be replaced by the newly calculated ones when you take this change.</>
							) : (
								<><strong>{label}</strong> becomes {kind}. {heldWords}; those values will be replaced by the
								calculated ones when you save.</>
							)}
						</Typography.Paragraph>
						{!counted ? null : hidden ? (
							<Typography.Text type='secondary' data-testid='driven-backfill-hidden'>It reads something hidden from you, so no values are shown.</Typography.Text>
						) : rows.length ? (
							<Table
								size='small'
								pagination={false}
								dataSource={rows}
								columns={[
									{ title: update ? 'Record' : 'Item', dataIndex: 'title', key: 'title', render: value => value || '—' },
									{ title: 'Now', dataIndex: 'current', key: 'current', render: value => formatCalculatedValue(element, value) },
									{ title: update ? 'After' : 'After save', dataIndex: 'next', key: 'next', render: value => formatCalculatedValue(element, value) },
								]}
							/>
						) : preview?.incomplete && withValues ? (
							<Typography.Text type='secondary' data-testid='driven-backfill-partial'>keepr stopped checking before it could show any, so there is no sample; the count is complete.</Typography.Text>
						) : (
							<Typography.Text type='secondary'>{withValues ? `The first ${noun}s checked already hold the calculated value.` : `No ${noun} holds a value in it yet.`}</Typography.Text>
						)}
						{preview?.incomplete && !hidden && rows.length ? (
							<Typography.Paragraph type='secondary' data-testid='driven-backfill-partial' style={{ marginTop: 8, marginBottom: 0 }}>
								keepr stopped checking early, so this is only a partial sample; the count is complete.
							</Typography.Paragraph>
						) : null}
						{withValues ? (
							<Typography.Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
								<ExternalLink href={reviewPath}>
									Review all {withValues.toLocaleString()} {noun}{withValues === 1 ? '' : 's'} with a value
								</ExternalLink>
								<Typography.Text type='secondary'>{update ? ' (opens in a new tab; nothing changes until you take it here)' : ' (opens in a new tab; the card stays unsaved until you confirm here)'}</Typography.Text>
							</Typography.Paragraph>
						) : null}
					</div>
				);
			})}
		</div>
	);
}
